import React, { useState, useContext } from "react";
import NVHorizontalBarChart, {
  formats,
} from "common/graphs/NVHorizontalBarChart";
import styled from "styled-components";
import BqnContext from "common/BqnContext";

const WrapperBqn = styled.div`
  .centered {
    display: flex;
    justify-content: center;
  }
`;

const ControlsWrapper = styled.div`
  margin: 10px 40px 50px 70px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const ControlItemWrapper = styled.div`
  display: inline-flex;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 0px 0 0px;
  gap: 5px;
  align-items: center;
  cursor: pointer;
  width: 15ch;

  .square {
    width: 12px;
    min-width: 12px;
    height: 12px;
    border: 2px solid;
    border-radius: 6px;
  }

  label {
    user-select: none;
    margin-bottom: 0;
    font-size: 13px;
    font-weight: normal;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const CONTROL_ITEMS = [
  {
    label: "Grouped",
    key: "Grouped",
    color: "#444444",
    field: "grouped",
  },
  {
    label: "Stacked",
    key: "Stacked",
    color: "#444444",
    field: "stacked",
  },
];

const getAspects = () => [
  {
    label: "Tx",
    color: "#1f77b4",
    field: "tx",
  },
  {
    label: "Rx",
    color: "#ffa500",
    field: "rx",
  },
];

const throughputByAspects = (usage = [], aspects) => {
  return aspects.map(({ label, color, field }) => {
    const values = usage.reduce((acc, value)=>{
      const {name, bqn} = value;
      if (name === "access" && bqn !== "all"){
        acc.push({label:"", value: new Number(undefined)})
      }
      acc.push({label: name, value: Number(value[field])})
     return acc;
    },[])
    return ({
    key: label,
    label,
    color,
    values,
  })});
}

const getMaxValue = (usage)=> {
  return Object.values(usage).reduce((acc, item)=>{
    const maxValues = item.reduce((accItem, {rx, tx})=> {
      const max = Math.max(...[rx,tx, acc]);
      return max;
    }, 0);
    return Math.max(...[maxValues, acc])
  }, 0);
}

const ClusterUsage = ({ usagePerBqn }) => {
  const maxValue = getMaxValue(usagePerBqn);
  const [stacked, setStacked] = useState(false);
  const aspects = getAspects();
  const [aspectsSelection, setAspectsSelection] = useState(
    new Set(aspects.map((aspect) => aspect.field))
  );

  const handleGroupedStacked = (clicked) => {
    setStacked(clicked === "stacked");
  };

  const handleAspectSelection = (aspect) => {
    if (aspectsSelection.has(aspect)) {
      aspectsSelection.delete(aspect);
    } else {
      aspectsSelection.add(aspect);
    }
    setAspectsSelection(new Set([...aspectsSelection]));
  };
  return (
    <>
      <ControlsWrapper>
        {CONTROL_ITEMS.map(({ label, key, color, field }) => {
          const selected =
            field === "grouped" ? stacked === false : stacked === true;
          return (
            <ControlItem
              label={label}
              key={key}
              color={color}
              isSelected={selected}
              field={field}
              onSelection={handleGroupedStacked}
            />
          );
        })}
        {aspects.map((aspect) => {
          const { label, color, field } = aspect;
          return (
            <ControlItem
              label={label}
              color={color}
              key={label}
              field={field}
              isSelected={aspectsSelection.has(field)}
              onSelection={handleAspectSelection}
            />
          );
        })}
      </ControlsWrapper>
      {Object.entries(usagePerBqn).map(([bqn, usage]) => {
        const selectedAspects = aspects.filter(({ field }) =>
          aspectsSelection.has(field)
        );
        const key = `thr-${bqn}-st${stacked === true ? "on" : "off"}${[...aspectsSelection].join('-')}`;
        
        return (
          <WrapperBqn key={`througput-${bqn}`}>
            <h6>BQN {bqn.toUpperCase()}</h6>
            <div>
              <NVHorizontalBarChart
                tooltipValueFormatter={(value) =>
                  `${formats.twoDecimals(value)} Mbps`
                }
                valueFormat={formats.twoDecimals}
                stacked={stacked}
                items={throughputByAspects(usage, selectedAspects)}
                key={key}
                id={key}
                showRoundCorners={true}
                showColors={false}
                valuePadding={30}
                hideLegends={true}
                margin={{ top: 0, right: 50, bottom: 10, left: 70 }}
                fixedYRange={[0, maxValue]}
              />
              <div className="centered">Mbps</div>
            </div>
          </WrapperBqn>
        );
      })}
    </>
  );
};

const ControlItem = ({ label, isSelected, onSelection, color, field }) => {
  const handleClick = () => {
    onSelection(field);
  };
  return (
    <ControlItemWrapper onClick={handleClick}>
      <div
        className="square"
        style={{
          backgroundColor: isSelected ? color : "transparent",
          borderColor: color,
        }}
      ></div>
      <label title={label}>{label}</label>
    </ControlItemWrapper>
  );
};

const GroupedOrStached = ({ items, bqn, barHeight, ...rest }) => {
  const [stacked, useStacked] = useState(false);
  const doSetStacked = () => useStacked(true);
  const doSetGrouped = () => useStacked(false);
  const controls = CONTROL_ITEMS.map(({ label, key, color, field }) => {
    return {
      label,
      key,
      color,
      field,
      doToggle: field === "grouped" ? doSetGrouped : doSetStacked,
      disabled: field === "grouped" ? stacked === true : stacked === false,
    };
  });

  return (
    <NVHorizontalBarChart
      barHeight={barHeight}
      prependLegends={controls}
      stacked={stacked}
      items={items}
      key={`througput-${bqn}-stacked${stacked === true ? "on" : "off"}`}
      {...rest}
    />
  );
};

const getFilteredUsage = ({ usage, bqnId, mode }) => {
  if (mode === "standalone" || bqnId === "all") {
    return usage;
  }
  return usage.filter(({ bqn }) => {
    return parseFloat(bqn) === parseFloat(bqnId);
  });
};

const Result = ({ usage = [] }) => {
  const {
    clusterStatus: { mode },
    bqnId,
  } = useContext(BqnContext);
  const filteredUsage = getFilteredUsage({ usage, bqnId, mode });
  const aspects = getAspects();
  if (mode === "standalone") {
    return (
      <WrapperBqn>
        <GroupedOrStached
          tooltipValueFormatter={(value) =>
            `${formats.twoDecimals(value)} Mbps`
          }
          valueFormat={formats.twoDecimals}
          items={throughputByAspects(filteredUsage, aspects)}
          showRoundCorners={true}
          showColors={false}
          valuePadding={30}
          key={`throughput${bqnId}`}
          barHeight={39}
        />
        <div className="centered">Mbps</div>
      </WrapperBqn>
    );
  }
  const usagePerBqn = filteredUsage.reduce((acc, usage) => {
    const { bqn } = usage;
    if (!acc[bqn]) {
      acc[bqn] = [usage];
    } else {
      acc[bqn] = [...acc[bqn], usage];
    }
    return acc;
  }, {});
  return <ClusterUsage usagePerBqn={usagePerBqn} />;
};

export default Result;
